// The address of ONE QC check — what every "Open check" link points at
// (F03 UF-05 / UF-06: the inspection card's QC hold, the truck's QC box, the
// pallet page's QC card, and a QC-failure non-conformance).
//
// /qc?check=<qc_run_id> opens that check's record over QC home, whatever state
// its run is in (QC.tsx reads the param). The record needs only the check id.
//
// A caller stamps its own location as the return path (useNavigateWithReturn /
// useReturnHereState), and closing the record goes back there.
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function qcCheckHref(check: { qc_run_id: string }): string {
  return `/qc?check=${encodeURIComponent(check.qc_run_id)}`
}

/** The `?check=` value, or null for anything that is not a check id — a
 *  hand-edited `?check=abc` must not reach the database as a 22P02. */
export function parseCheckId(raw: string): string | null {
  const v = raw.trim()
  return UUID_RE.test(v) ? v.toLowerCase() : null
}

/**
 * The FAILed check behind a pallet's QC verdict (UF-06): the pallet page's
 * card reads qc_results_unified, whose rows carry no check id, so the card
 * asks qc_runs once. Across the pallet's source runs (own run ∪ build-up
 * lines): an unreleased FAIL first, then the newest. Null when the FAIL came
 * from a legacy table that has no check record to open.
 */
export async function readFailedCheck(tenantId: string, runIds: readonly string[]): Promise<string | null> {
  if (runIds.length === 0) return null
  const { data, error } = await withReadTimeout(
    supabase.from('qc_runs')
      .select('qc_run_id')
      .eq('tenant_id', tenantId)
      .eq('result', 'FAIL').is('deleted_at', null)
      .in('source_run_id', [...runIds])
      .order('dispositioned_at', { ascending: false, nullsFirst: true })
      .order('completed_at', { ascending: false, nullsFirst: false })
      .order('qc_run_id')
      .limit(1),
    'QC: failed check on a pallet',
  )
  if (error) throw error
  return ((data ?? [])[0] as { qc_run_id: string } | undefined)?.qc_run_id ?? null
}

export function useFailedQcCheck(runIds: readonly string[], enabled: boolean) {
  const tenantId = useActiveTenantId()
  const ids = [...new Set(runIds.filter(Boolean))].sort()
  return useQuery({
    queryKey: ['qc-failed-check', tenantId, ids.join(',')],
    enabled: enabled && !!tenantId && ids.length > 0,
    queryFn: () => readFailedCheck(tenantId as string, ids),
  })
}
